
import axios from 'axios';
import {computed, defineComponent, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {ElIcon, ElInput, ElFormItem} from "element-plus";
import {Check, WarnTriangleFilled} from "@element-plus/icons-vue";
import {ApiGetters, FormActions, FormGetters} from './../../Store/index';
import {
  addNewValueToFunnelStep,
  callPaq, getInputsAboveOrder, resetInputs, setResettingFormToFalseWhenNoRequests,
  updateStoreValueAndFilter, useShowElementByPredefinedManufacturer,
  validation
} from './../../Composables/form';
import {toggleLoadingAnimation} from './../../Composables/api';
import {isEmpty} from './../../Composables/functions';

export default defineComponent({
    name: "InputComponent",
    components: {
      ElFormItem,
      ElInput,
      ElIcon,
      Check,
      WarnTriangleFilled
    },

    props: {
      value: String,
      title: String,
      classInput: String,
      classDiv: String,
      maxlength: Number,
      type: String,
      size: String,
      placeholder: String,
      append: String,
      prepend: String,
      name: {
        type: String,
        default: ''
      },
      id: String,
      errorMessage: String,
      pattern: String,
      required: {
        type: Boolean,
        default: false,
      },
      max: Number,
      min: Number,
      step: Number,
      filterProperty: {
        type: Array,
        required: false,
        default: () => ['', '']
      },
      defaultValue: {
        type: String,
        default: "",
      },
    },

    setup(props) {
      // Access the Vuex store
      const store = useStore();

      // Getters from the store
      const labels = store.getters[`form/${FormGetters.GET_LABELS}`],
          icons = store.getters[`form/${FormGetters.GET_ICONS}`],
          filterName = (props.filterProperty[1] as string).toString(),
          debounceTimer = ref<number | null>(null),
          inputFieldRef = ref<HTMLInputElement | null>(null);

      let value = ref(''),
          isValid = ref(false),
          source = ref(axios.CancelToken.source()),
          isRequired = ref(props.required),
          isFloatingLabel = ref(false),
          isTouched = ref(false),
          clearable = ref(true),
          showError = ref(false),
          showElement = computed(() => useShowElementByPredefinedManufacturer(store, props.name).value),
          wasClickedOnShowResultPage = ref(store.getters[`form/${FormGetters.GET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`]);

      // Default value initialization
      onMounted(() => {
        if (props.defaultValue) {
          value.value = props.defaultValue;
          updateValue(props.defaultValue);
        }

        // _paq
        if (filterName && value.value && !store.getters[`form/${FormGetters.GET_IS_RESETTING_FORM}`]) {
          callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', filterName);
        }
      });

      // Function to reset values
      const setFloatingLabel = () => {
        isFloatingLabel.value = true;
      };

      // Function to reset values
      const resetFloatingLabel = () => {
        // @ts-ignore
        if (isEmpty(value.value) && document.activeElement !== inputFieldRef.value?.input) {
          isFloatingLabel.value = false;
        }
      };

      // Check if the component should be clearable
      const handleClearable = () => {
        clearable.value = !!value.value;
      };

      // Function to reset values
      const resetValues = () => {
        isValid.value = false;
        isTouched.value = false;
        value.value = '';

        const filter = store.getters[`form/${FormGetters.GET_DATA}`].filter.value;

        if (isEmpty(filter) || isEmpty(filter[filterName])) {
          store.dispatch(`form/${FormActions.DELETE_DATA}`, {field: 'filter', key: 'value', value: filterName});
        }

        // Reset floating label
        resetFloatingLabel();
      };

      // Function to update the selected option value and validity
      const clear = async () => {
        await store.dispatch(`form/${FormActions.SET_IS_RESSETING_FORM}`, true);

        // Reset values
        resetValues();

        // Update store value and filter
        await updateStoreValueAndFilter(store, props, value.value, isValid.value, false, true);

        await validation(store);

        // Reset floating label
        resetFloatingLabel();

        resetInputs(store, getInputsAboveOrder(store, props.name), 'resetValue');

        await setResettingFormToFalseWhenNoRequests(store);

        focusFirstInput();
      };

      const parentRef = ref(null);

      const focusFirstInput = () => {
        if (parentRef.value) {
          const firstInput = (parentRef.value as HTMLElement).querySelector('input');
          if (firstInput) {
            firstInput.focus();
          }
        }
      };

      const validateInput = async () => {
        // Check if the input is required
        if (isRequired.value) {
          // Set isValid based on whether the value has length for required input
          (value.value.length > 0) ? isValid.value = true : isValid.value = false;
        }

        // Check if a pattern is provided
        if (props.pattern) {
          // Create a regular expression from the pattern
          let regPattern = new RegExp(props.pattern);

          // Check if the input type is email and matches the email pattern
          if (props.type == 'email' && regPattern.test(value.value)) {
            // Set loading state
            await toggleLoadingAnimation(true);

            // Initialize isValid to false
            isValid.value = false;

            // Create URLSearchParams with the email value
            let params = new URLSearchParams({email: value.value});

            const CancelToken = axios.CancelToken;

            try {
              // Check if a CancelToken.source has been assigned to the source variable, then call the cancel method when true.
              if (source) {
                source.value.cancel();
              }
              source.value = CancelToken.source();

              // Perform an asynchronous POST request to validate email
              await axios
                  .post('/validate-email.json', params, {cancelToken: source.value.token })
                  .then((response) => {
                    // Update isValid based on the response data
                    isValid.value = response.data;
                  });

              // Reset loading state
              if (store.getters[`api/${ApiGetters.GET_ON_GOING_REQUEST}`] === 0) {
                setTimeout(async () => {
                  // Fetch new data
                  await toggleLoadingAnimation(false);
                }, 2000);
              }
            } catch (e) {
              // Check if the request property is undefined to handle cancellation error
              if ((e as any).request === undefined) {
                if (store.getters[`api/${ApiGetters.GET_ON_GOING_REQUEST}`] === 0) {
                  // Handle cancelling error
                  setTimeout(async () => {
                    // Fetch new data
                    await toggleLoadingAnimation(false);
                  }, 1500);
                }
              }
            }
          } else if (props.type === 'number') {
            // Use a regular expression to check if the value is a valid number
            // Set isValid based on whether the value is a valid number
            isValid.value = /^-?\d*\.?\d+$/.test(value.value);
          } else {
            // Check if the value matches the pattern for non-email input
            regPattern.test(value.value) ? isValid.value = true : isValid.value = false;
          }
        }

        // Check if a maximum value is provided
        if (props.max && isValid.value) {
          // Convert the value to an integer after removing commas and dots
          let v = parseInt(value.value.replace(/\.|,/g, ""), 10);
          // Set isValid based on whether the value is less than the maximum
          (v > props.max) ? isValid.value = false : isValid.value = true;
        }

        // Check if a minimum value is provided
        if (props.min && isValid.value) {
          // Convert the value to an integer after removing commas and dots
          let v = parseInt(value.value.replace(/\.|,/g, ""), 10);
          // Set isValid based on whether the value is greater than the minimum
          (v < props.min) ? isValid.value = false : isValid.value = true;
        }

        // Check if a maximum length is provided
        if (props.maxlength && isValid.value) {
          // Set isValid based on whether the value exceeds the maximum length
          (value.value.length > props.maxlength) ? isValid.value = false : isValid.value = true;
        }
      }

      const showErrorMessage = () => {
        showError.value = (isTouchedAndRequired() && !isValid.value) || (wasClickedOnShowResultPage.value && isRequired.value && !isValid.value);
      };

      // Function to update the selected option value and validity
      const updateValue = async (newValue: any) => {
        await store.dispatch(`form/${FormActions.SET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`, false);

        const predefinedValues = store.getters[`form/${FormGetters.GET_DATA}`].predefinedManufacturerValues;

        if (predefinedValues && predefinedValues.hasValue && predefinedValues[props.name]) {
          newValue = predefinedValues[props.name];
        }

        // Update the validity based on whether the value is empty or not
        await validateInput();

        if (props.name === 'fieldHSN') {
          newValue = newValue.substring(0, 4);
        }
        if (newValue.length > 1 && props.name === 'fieldTSN') {
          newValue = newValue.toUpperCase().substring(0, 3);
        }

        isTouched.value = true;
        value.value = newValue;

        if (isEmpty(value.value)) {
          resetValues();
        }

        if (isValid.value) {
          const fetchNewData = props.name !== 'fieldEmail';
          await updateStoreValueAndFilter(store, props, value.value, isValid.value, false, true, false, fetchNewData);
          addNewValueToFunnelStep(store, filterName, value.value);

          // _paq
          if (filterName && value.value) {
            callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', filterName);
          }

          await validation(store);
          handleClearable();
        }

        showErrorMessage();
      }

      watch(() => props.required, (newValue) => {
         isRequired.value = newValue;
      });

      // Track changes in the value
      watch(() => props.value, async (newValue) => {
        if (newValue !== undefined) {
          await updateValue(newValue);
        }
      });

      // Watch for changes in the value
      watch(() => store.getters[`form/${FormGetters.GET_INPUTS}`][props.name].value, async (newValue, oldValue) => {
        if (newValue === 'resetValue') {
          await clear();
          await updateValue('');
        }
      });

      watch(() => store.getters[`form/${FormGetters.GET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`], async (newValue, oldValue) => {
        wasClickedOnShowResultPage.value = newValue;
        showErrorMessage();
      });

      const isTouchedAndRequired = () => {
        return isTouched.value && isRequired.value;
      }

      // Function to perform the debounced update
      const debouncedUpdate = async () => {
        // Clear the existing timer
        if (debounceTimer.value !== null) {
          clearTimeout(debounceTimer.value);
        }

        const timeOut = props.name === 'mileage' ? 1500 : 0;

        // Set a new timer to call updateValue after a delay (e.g., 500 milliseconds)
        debounceTimer.value = setTimeout(async () => {
          await updateValue(value.value);
        }, timeOut);
      };

      const canShowLabel = computed(() => {
        return !props.title; // we show label only if no title is used
      });

      return {
        icons,
        labels,
        value,
        isValid,
        isTouched,
        isRequired,
        isTouchedAndRequired,
        resetValues,
        updateValue,
        debouncedUpdate,
        isFloatingLabel,
        clearable,
        inputFieldRef,
        showError,
        showElement,
        clear,
        setFloatingLabel,
        showErrorMessage,
        resetFloatingLabel,
        parentRef,
        canShowLabel
      }
    }
  })
